import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Award,
  Sprout,
  FlaskConical,
  ChevronDown,
  Truck,
} from 'lucide-react';

/**
 * "Why £44.99?" rationale strip — collected pricing-justification panel.
 *
 * Audit gap: the consultant noted pricing rationale was dispersed
 * across the site. This block collects it into 5 instantly-scannable
 * icons + sub-labels so a visitor understands what they're paying for
 * within ~3 seconds.
 */
const WhyThisPrice = () => {
  const pillars = [
    {
      Icon: Award,
      title: 'Patented BCM-95®',
      sub: 'Clinical-strength, high-bioavailability curcumin',
    },
    {
      Icon: ShieldCheck,
      title: 'Batch-Tested Purity',
      sub: 'Rigorous quality control & safety standards',
    },
    {
      Icon: Sprout,
      title: 'Single-Plant Source',
      sub: '100% pure botanical extract · No synthetic fillers',
    },
    {
      Icon: FlaskConical,
      title: '8-Hour Active Delivery',
      sub: 'Sustained release for steady levels through the day',
    },
    {
      Icon: Truck,
      title: 'All-Inclusive Pricing',
      sub: 'UK VAT + FREE standard delivery',
    },
  ];
  return (
    <section
      className="bg-stone-50 border border-stone-200 rounded-2xl p-5 sm:p-6 my-6"
      data-testid="why-this-price"
    >
      <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-3.5">
        Why <span className="text-emerald-700">£44.99</span> is fair value
        <span className="block text-xs sm:text-[13px] font-normal text-slate-500 mt-0.5">
          What you&apos;re paying for, in five lines.
        </span>
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {pillars.map(({ Icon, title, sub }) => (
          <div
            key={title}
            className="flex flex-col items-center text-center px-2 py-4 rounded-xl bg-white border border-stone-200"
            data-testid={`price-pillar-${title.toLowerCase().replace(/\W+/g, '-')}`}
          >
            <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center mb-2.5 shadow-sm">
              <Icon className="w-5 h-5 text-emerald-700" strokeWidth={2.2} />
            </div>
            <div className="text-[12px] sm:text-[13px] font-bold text-slate-900 leading-tight">{title}</div>
            <div className="text-[10px] sm:text-[11px] text-slate-500 leading-snug mt-0.5">{sub}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

/**
 * 14-Day UK Returns trust badge — surfaces the UK Consumer Contracts
 * Regulations cancellation right that we already honour on `/returns`.
 *
 * Audit gap: money-back / refund clarity not surfaced as a trust signal.
 */
export const ReturnsTrustBadge = ({ compact = false }) => (
  <div
    className={`flex items-center gap-3 rounded-xl bg-emerald-50/70 border border-emerald-200 text-emerald-900 ${compact ? 'px-3.5 py-2.5' : 'px-4 py-3'}`}
    data-testid="returns-trust-badge"
  >
    <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0" />
    <div className="text-[12px] sm:text-[13px] leading-snug">
      <strong className="font-bold">14-Day UK Returns &middot; Buyer-Protected</strong>
      <span className="block text-emerald-800/85 text-[11px] sm:text-[12px] mt-0.5">
        Under UK Consumer Contracts Regulations &mdash; full refund on unopened bottles within 14 days.{' '}
        <a href="/returns" className="underline underline-offset-2 hover:no-underline font-semibold">Read policy</a>
      </span>
    </div>
  </div>
);

/**
 * Objection-handling FAQ accordion. Plain-English answers to the 5
 * questions the consultant flagged as raised but unanswered: "Why £47?",
 * "Will it work for me?", "Safety with medication?", "How long?", "Refund?".
 *
 * MHRA/ASA-compliant — uses 'supports' / 'food supplement' framing.
 */
const QA = [
  {
    q: 'Why does a 1-month bottle cost £44.99?',
    a: (
      <>
        <p>The price reflects four things: (1) the <strong>patented BCM-95®</strong> formulation we use (not generic turmeric powder), (2) <strong>UK manufacturing</strong> under ISO 9001/14001/22000 quality systems, (3) a <strong>650&nbsp;mg standardised dose</strong> per capsule (most supermarket capsules deliver 15&ndash;30&nbsp;mg of curcuminoids), and (4) <strong>all-inclusive pricing</strong> &mdash; UK VAT and standard delivery are bundled, not added at checkout.</p>
        <p className="mt-2">Buy more to save: 2 bottles work out at <strong>£35.00</strong> each and 3 bottles at <strong>£31.00</strong> each (4+ bottles also £31.00) &mdash; all-inclusive, with your saving shown at checkout.</p>
      </>
    ),
  },
  {
    q: 'Will BCM-95® work for me?',
    a: (
      <>
        <p>BCM-95® is a <strong>food supplement</strong>, not a medicine. We can&apos;t legally promise a specific outcome &mdash; UK MHRA/ASA rules don&apos;t allow it, and we follow them strictly. What we <em>can</em> tell you is:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>The active formulation (BCM-95®) is the same one referenced in published pharmacokinetic literature.</li>
          <li>It is taken once a day, with food.</li>
          <li>People typically use curcumin supplements as part of a daily routine to <em>support</em> joint and inflammatory health.</li>
          <li>If you&apos;re hoping for an immediate, dramatic effect, please skip this product. If you&apos;re looking for a long-term, well-researched daily phytonutrient, you&apos;re in the right place.</li>
        </ul>
      </>
    ),
  },
  {
    q: 'Is it safe to take with prescription medication?',
    a: (
      <>
        <p>Curcumin is a food, but it can interact with certain medications &mdash; particularly blood-thinners (warfarin), diabetes medication, and gallbladder/bile-related medication. Some piperine-paired curcumin supplements also affect drug-metabolism enzymes (CYP3A4) &mdash; <strong>BCM-95® contains no piperine</strong>, but the curcuminoids themselves can still interact.</p>
        <p className="mt-2"><strong>Please consult your GP or pharmacist before taking this product if you are on prescription medication, pregnant, breastfeeding, or under medical supervision.</strong> If they say yes, we&apos;d love to have you. If they say wait, please wait.</p>
      </>
    ),
  },
  {
    q: 'How long until I notice anything?',
    a: (
      <>
        <p>Honest answer: it varies, and we can&apos;t legally promise an outcome. Curcumin is a slow-acting phytonutrient &mdash; most published clinical studies run for <strong>8&ndash;12 weeks</strong>. We&apos;d suggest you commit to at least one full bottle (60 capsules &asymp; 2 months) before judging whether it&apos;s right for you.</p>
        <p className="mt-2">If you&apos;d like to try first without a long commitment, a single bottle is <strong>£37.49</strong>, all-inclusive of UK VAT and delivery.</p>
      </>
    ),
  },
  {
    q: 'What if it isn\u2019t for me \u2014 can I return it?',
    a: (
      <>
        <p>Yes. Under UK Consumer Contracts Regulations you have <strong>14 days from delivery</strong> to request a return on an unopened bottle for a full refund. We also help with damaged-in-transit replacements and any genuine quality concern beyond the 14-day window.</p>
        <p className="mt-2"><a href="/returns" className="text-emerald-700 underline font-semibold">Read the full returns policy &rarr;</a></p>
      </>
    ),
  },
];

const ObjectionFAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="my-8" data-testid="objection-faq">
      <h3 className="text-xl sm:text-2xl font-bold font-heading text-slate-900 mb-1.5">
        Honest answers to the questions buyers actually ask
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Everything you need to know before making your decision.
      </p>
      <div className="space-y-2.5">
        {QA.map((row, idx) => {
          const isOpen = open === idx;
          return (
            <div
              key={row.q}
              className="rounded-xl border border-stone-200 bg-white overflow-hidden"
              data-testid={`objection-faq-item-${idx}`}
            >
              <button
                onClick={() => setOpen(isOpen ? -1 : idx)}
                className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 text-left hover:bg-stone-50 transition-colors"
                aria-expanded={isOpen}
                data-testid={`objection-faq-toggle-${idx}`}
              >
                <span className="font-semibold text-slate-900 text-[14px] sm:text-[15px]">{row.q}</span>
                <ChevronDown
                  className={`w-4 h-4 text-slate-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.2 }}
                  className="px-4 sm:px-5 pb-4 text-[13px] sm:text-[14px] text-slate-700 leading-relaxed border-t border-stone-100"
                >
                  <div className="pt-3">{row.a}</div>
                </motion.div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export { WhyThisPrice, ObjectionFAQ };
export default WhyThisPrice;
